"use client";

import { useEffect } from "react";
import { Radar, Gift, PiggyBank, Bus } from "lucide-react";
import { T, useTextLocale, tl } from "@/components/t";
import { SectionHeader, Tint } from "@/components/finance/Finance";
import { Badge, Icon, Stack } from "@/design";
import { track } from "@/lib/analytics";
import { P } from "@/lib/finance-engine";
import styles from "./BenefitRadar.module.css";

/** Benefit-Radar — scans the recognised payslip lines (deductions + what_you_get
 *  labels) for VWL / bAV / Jobticket signals and flags the MISSING levers as
 *  honest "prüfen, nicht voraussetzen" cards. Pure text detection on labels only
 *  (explanations/tips are excluded on purpose: a tip that MENTIONS bAV would
 *  otherwise read as "already has bAV"). */
export default function BenefitRadar({
  deductions,
  whatYouGet,
  example,
}: {
  deductions?: { label: string }[];
  whatYouGet?: { label: string }[];
  /** true for the /example payload — renders, but never tracks */
  example?: boolean;
}) {
  const loc = useTextLocale();

  const hay = [
    ...(deductions || []).map((d) => d.label || ""),
    ...(whatYouGet || []).map((w) => w.label || ""),
  ]
    .join(" | ")
    .toLowerCase();

  const hasVwl = /vwl|vermögenswirksam|vermoegenswirksam/.test(hay);
  const hasBav = /\bbav\b|betriebliche altersvorsorge|entgeltumwandlung|direktversicherung|pensionskasse|pensionsfonds|unterstützungskasse/.test(hay);
  const hasTicket = /jobticket|deutschlandticket|d-ticket|öpnv|oepnv|fahrtkostenzuschuss/.test(hay);

  const bavPct = Math.round(P.bavAG * 100); // 15 — engine constant, not re-derived
  const levers = [
    {
      key: "vwl",
      present: hasVwl,
      icon: Gift,
      title: <T de="Vermögenswirksame Leistungen (VWL)" en="Capital-forming benefits (VWL)" />,
      desc: (
        <T
          de="Bis zu 40€/Monat Zuschuss vom Arbeitgeber möglich — ob und wie viel, regelt dein Arbeits- oder Tarifvertrag. Auf dieser Abrechnung ist keine VWL-Position zu erkennen: Nachfragen bei HR kostet nichts."
          en="Up to 40€/month employer contribution is possible — whether and how much depends on your employment or collective agreement. No VWL line is visible on this payslip: asking HR costs nothing."
        />
      ),
      caveat: (
        <T
          de="Nicht jeder Arbeitgeber bietet VWL — prüfen, nicht voraussetzen."
          en="Not every employer offers VWL — check, don't assume."
        />
      ),
    },
    {
      key: "bav",
      present: hasBav,
      icon: PiggyBank,
      title: <T de="Betriebliche Altersvorsorge (bAV)" en="Company pension (bAV)" />,
      desc: (
        <T
          de={`Zahlst du per Entgeltumwandlung ein, muss dein Arbeitgeber ${bavPct}% Zuschuss geben (gesetzliche Pflicht, soweit er dabei Sozialabgaben spart). Bis ${P.bavSteuerfreiMo.toLocaleString("de-DE")}€/Monat steuerfrei (2026). Auf dieser Abrechnung ist keine bAV-Position zu erkennen.`}
          en={`If you convert salary into a company pension, your employer must add a ${bavPct}% top-up (statutory duty where they save social contributions). Tax-free up to ${P.bavSteuerfreiMo.toLocaleString("de-DE")}€/month (2026). No bAV line is visible on this payslip.`}
        />
      ),
      caveat: (
        <T
          de="Ob sich das konkrete Angebot lohnt, hängt von Kosten und Vertrag ab — keine Anlageberatung; bei größeren Beträgen unabhängig beraten lassen."
          en="Whether the specific offer is worth it depends on costs and contract — not investment advice; get independent advice for larger amounts."
        />
      ),
    },
    {
      key: "jobticket",
      present: hasTicket,
      icon: Bus,
      title: <T de="Jobticket / Deutschlandticket" en="Job ticket / Deutschlandticket" />,
      desc: (
        <T
          de="Viele Arbeitgeber bezuschussen das Deutschlandticket oder ein Jobticket — der Zuschuss kann steuerfrei on top aufs Gehalt kommen und ersetzt private Ticketkosten. Auf dieser Abrechnung ist kein Ticket-Posten zu erkennen."
          en="Many employers subsidise the Deutschlandticket or a job ticket — the subsidy can be tax-free on top of your salary and replaces private ticket costs. No ticket line is visible on this payslip."
        />
      ),
      caveat: (
        <T
          de="Nicht jeder Arbeitgeber bietet das — und manche zahlen Tickets direkt, ohne dass es auf der Abrechnung auftaucht."
          en="Not every employer offers this — and some pay for tickets directly without it ever showing on the payslip."
        />
      ),
    },
  ];
  const missing = levers.filter((l) => !l.present);

  useEffect(() => {
    if (example || !deductions || deductions.length === 0 || missing.length === 0) return;
    track("payslip_benefitradar_flag", {
      flagged: missing.map((l) => l.key).join(","),
      missing: missing.length,
      has_vwl: hasVwl,
      has_bav: hasBav,
      has_ticket: hasTicket,
    });
    // one flag event per analysed payslip (component mounts per result view)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!deductions || deductions.length === 0) return null;

  if (missing.length === 0) {
    return (
      <div>
        <SectionHeader
          title={<T de="Benefit-Radar" en="Benefit radar" />}
          hint={<T de="Ungenutzte Gehalts-Hebel" en="Unused salary levers" />}
        />
        <Tint tone="green">
          <div className={styles.allGood}>
            <Icon icon={Radar} decorative />
            <span>
              <T de="Stark — VWL, bAV und Jobticket tauchen auf deiner Abrechnung bereits auf." en="Nice — VWL, company pension and job ticket already show up on your payslip." />
            </span>
          </div>
        </Tint>
      </div>
    );
  }

  return (
    <div>
      <SectionHeader
        title={<T de="Benefit-Radar: ungenutzte Hebel" en="Benefit radar: unused levers" />}
        hint={tl(loc, `${missing.length} von 3 Hebeln nicht auf der Abrechnung gefunden`, `${missing.length} of 3 levers not found on this payslip`)}
      />
      <Stack gap="4">
        {missing.map((l) => (
          <Tint key={l.key} tone="green">
            <div className={styles.head}>
              <span className={styles.icon}><Icon icon={l.icon} decorative /></span>
              <p className={styles.title}>{l.title}</p>
              <Badge tone="success"><T de="Prüfen" en="Check" /></Badge>
            </div>
            <p className={styles.desc}>{l.desc}</p>
            <p className={styles.caveat}>{l.caveat}</p>
          </Tint>
        ))}
        <p className={styles.disclaimer}>
          <T
            de="Basis ist nur diese eine Abrechnung: Was dein Arbeitgeber nicht über die Lohnabrechnung abwickelt, sieht der Radar nicht. Finanzbildung, keine Finanz- oder Anlageberatung."
            en="Based on this one payslip only: anything your employer doesn't run through payroll is invisible to the radar. Financial education, not financial or investment advice."
          />
        </p>
      </Stack>
    </div>
  );
}
